if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item']
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.thumbnailWheelHandler = null;
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev')
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction'
          },
          threshold: 2
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal'
            }
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          }
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap
              }
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5
              },
              1536: {
                slidesPerView: 7
              }
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();
            this.initThumbnailWheel();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      disconnectedCallback() {
        this.destroyThumbnailWheel();
      }

      initThumbnailWheel() {
        if (this.thumbnailWheelHandler || !this.elements.thumbnails) return;

        this.thumbnailWheelHandler = (event) => {
          if (FoxTheme.config.mqlMobile || this.mediaLayout !== 'vertical-carousel') return;

          const thumbsSlider = this.thumbsInstance?.slider;
          if (!thumbsSlider) return;

          const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
          if (delta === 0) return;

          const canMoveNext = delta > 0 && !thumbsSlider.isEnd;
          const canMovePrev = delta < 0 && !thumbsSlider.isBeginning;
          if (!canMoveNext && !canMovePrev) return;

          event.preventDefault();

          if (delta > 0) {
            thumbsSlider.slideNext();
          } else {
            thumbsSlider.slidePrev();
          }
        };

        this.elements.thumbnails.addEventListener('wheel', this.thumbnailWheelHandler, { passive: false });
      }

      destroyThumbnailWheel() {
        if (!this.thumbnailWheelHandler || !this.elements?.thumbnails) return;

        this.elements.thumbnails.removeEventListener('wheel', this.thumbnailWheelHandler);
        this.thumbnailWheelHandler = null;
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
          this.initThumbnailWheel();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;
            const thumbImage = media.querySelector('img');
            const thumbSrc = thumbImage ? thumbImage.currentSrc || thumbImage.src : src;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
              thumbSrc: thumbSrc,
              alt: thumbImage ? thumbImage.alt || '' : ''
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          paddingFn: () => {
            const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
            return {
              top: isMobile ? 16 : 0,
              bottom: isMobile && dataSource.length > 1 ? 88 : 0,
              left: 0,
              right: 0
            };
          },
          // Don't restore focus to the opener thumbnail on close. The opener lives
          // inside the slide it was opened from, so when the tab is re-activated the
          // browser re-focuses (and scrolls) that element into view, hijacking the
          // swiper back to the opening index. Keeping focus off it prevents the jump.
          returnFocus: false
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          return this.querySelector(`.product__media-item[data-media-index="${id}"] img`) || thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          const { currIndex } = this.lightbox?.pswp;
          const slider = this.sliderInstance?.slider;
          if (slider) {
            slider.slideTo(currIndex, 0, false);
          }
          this.renderLightboxThumbnails(dataSource);
        });

        this.lightbox.on('afterInit', () => {
          this.renderLightboxThumbnails(dataSource);
          window.setTimeout(() => this.renderLightboxThumbnails(dataSource), 80);
        });

        this.lightbox.on('openingAnimationEnd', () => {
          this.renderLightboxThumbnails(dataSource);
        });

        this.lightbox.on('imageSizeChange', () => {
          this.renderLightboxThumbnails(dataSource);
        });

        this.lightbox.on('resize', () => {
          this.renderLightboxThumbnails(dataSource);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox?.pswp;
          if (!this.sliderInstance && currIndex !== null) {
            const target = this.elements.mediaItems?.find((item) => Number(item.dataset.mediaIndex) === currIndex);
            if (target) {
              requestAnimationFrame(() => {
                const rect = target.getBoundingClientRect();
                const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (!inView) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              });
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              }
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              }
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            }
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          }
        });
      }

      renderLightboxThumbnails(dataSource) {
        const pswp = this.lightbox?.pswp;
        const root = pswp?.element || document.querySelector('.pswp');
        if (!root || !Array.isArray(dataSource) || dataSource.length < 2) return;

        let footer = root.querySelector('.pswp__metamall-footer');
        if (!footer) {
          footer = document.createElement('div');
          footer.className = 'pswp__metamall-footer';

          const counter = document.createElement('div');
          counter.className = 'pswp__metamall-counter';
          counter.setAttribute('aria-live', 'polite');
          footer.appendChild(counter);

          const thumbs = document.createElement('div');
          thumbs.className = 'pswp__metamall-thumbs';
          thumbs.setAttribute('role', 'list');

          dataSource.forEach((item, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'pswp__metamall-thumb';
            button.dataset.index = String(index);
            button.setAttribute('aria-label', `${index + 1}/${dataSource.length}`);

            const image = document.createElement('img');
            image.src = item.thumbSrc || item.src || '';
            image.alt = item.alt || '';
            image.loading = 'lazy';
            button.appendChild(image);
            thumbs.appendChild(button);
          });

          thumbs.addEventListener('click', (event) => {
            const button = event.target.closest('.pswp__metamall-thumb');
            if (!button || !this.lightbox?.pswp) return;
            const index = Number(button.dataset.index);
            if (Number.isNaN(index)) return;
            this.lightbox.pswp.goTo(index);
          });

          footer.appendChild(thumbs);
          root.appendChild(footer);
        }

        const activeIndex = pswp?.currIndex || 0;
        const counter = footer.querySelector('.pswp__metamall-counter');
        if (counter) counter.textContent = `${activeIndex + 1}/${dataSource.length}`;

        let activeButton = null;
        const thumbs = footer.querySelector('.pswp__metamall-thumbs');
        footer.querySelectorAll('.pswp__metamall-thumb').forEach((button) => {
          const isActive = Number(button.dataset.index) === activeIndex;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-current', isActive ? 'true' : 'false');
          if (isActive) activeButton = button;
        });

        this.centerActiveLightboxThumb(thumbs, activeButton);
        this.positionLightboxThumbnailFooter(root, footer);
        requestAnimationFrame(() => this.positionLightboxThumbnailFooter(root, footer));
      }

      centerActiveLightboxThumb(thumbs, activeButton) {
        if (!thumbs || !activeButton) return;
        const thumbLeft = activeButton.offsetLeft;
        const targetLeft = thumbLeft - (thumbs.clientWidth - activeButton.offsetWidth) / 2;
        const maxLeft = Math.max(0, thumbs.scrollWidth - thumbs.clientWidth);
        const nextLeft = Math.max(0, Math.min(maxLeft, targetLeft));
        if (Math.abs(thumbs.scrollLeft - nextLeft) < 2) return;
        thumbs.scrollTo({ left: nextLeft, behavior: 'smooth' });
      }

      positionLightboxThumbnailFooter(root, footer) {
        const pswp = this.lightbox?.pswp;
        if (!root || !footer || !pswp || !window.matchMedia('(max-width: 767.98px)').matches) return;

        const activeRoot = pswp.currSlide?.container || pswp.currSlide?.holderElement || null;
        const activeMedia = activeRoot?.querySelector?.('.pswp__img, img') || pswp.currSlide?.content?.element || null;
        const rect = activeMedia?.getBoundingClientRect?.();
        if (!rect || rect.width <= 1 || rect.height <= 1) return;

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const footerHeight = footer.offsetHeight || 78;
        const maxTop = Math.max(0, viewportHeight - footerHeight - 6);
        const top = Math.min(rect.bottom + 6, maxTop);

        footer.style.setProperty('--metamall-pswp-footer-top', `${Math.max(0, Math.round(top))}px`);
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image'
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance.slider) {
          const slideIndex = featuredMedia.position || 0;
          this.sliderInstance.slider.slideTo(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image'
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
